import React from "react";
import { KgTableIcon } from "../../atoms/KgTableIcon";
import type { GetDataStudioTableProfileResponse } from "../../../services/dataStudio/dataStudioTables.types";
import {
  MetadataPill,
  ProfileHeader,
  QualityScore,
  QualitySummary,
  TableIdentity,
  TableMetadata,
  TableTitle,
  TableTitleIcon,
  TableTitleRow,
} from "./DataStudioProfileTable.styles";

interface DataStudioProfileHeaderProps {
  table: GetDataStudioTableProfileResponse["table"];
}

export const DataStudioProfileHeader: React.FC<
  DataStudioProfileHeaderProps
> = ({ table }) => {
  /**
   * `row_count`, `column_count`, and `data_quality_score` all live under the
   * asset API's optional `attributes`, so a conformant response can omit any
   * of them. A pill is dropped rather than rendered as "undefined rows".
   */
  const summaryPills = [
    typeof table.rowCount === "number"
      ? `${table.rowCount.toLocaleString()} rows`
      : null,
    typeof table.columnCount === "number"
      ? `${table.columnCount} columns`
      : null,
    `${table.classificationCounts.dimensions} dimensions`,
    `${table.classificationCounts.measures} measures`,
  ].filter((pill): pill is string => pill !== null);

  return (
    <ProfileHeader>
      <TableIdentity>
        <TableTitleRow>
          <TableTitleIcon>
            {table.source.logoUrl ? (
              <img src={table.source.logoUrl} alt="" />
            ) : (
              <KgTableIcon size={16} />
            )}
          </TableTitleIcon>
          <TableTitle>{table.name}</TableTitle>
          {table.quality ? (
            <QualitySummary>
              <QualityScore $qualityStatus={table.quality.status}>
                {table.quality.score}%
              </QualityScore>{" "}
              quality
            </QualitySummary>
          ) : null}
        </TableTitleRow>
        <TableMetadata aria-label="Table profile summary">
          {summaryPills.map((pill) => (
            <MetadataPill key={pill}>{pill}</MetadataPill>
          ))}
        </TableMetadata>
      </TableIdentity>
    </ProfileHeader>
  );
};

export default DataStudioProfileHeader;
